import React, { useState, useEffect, useMemo, useRef } from 'react';
import { GoogleMap, useJsApiLoader, Polygon, Marker, InfoWindow, OverlayView, OverlayViewF } from '@react-google-maps/api';
import {
  InputLabel,
  Typography,
  FormControl,
  IconButton,
  Divider,
  Checkbox,
  FormControlLabel,
  useMediaQuery,
  Drawer,
  Autocomplete,
  Box,
  Button,
  TextField
} from '@mui/material';
import { ChevronLeft, ChevronRight } from '@mui/icons-material';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import NavigationIcon from '@mui/icons-material/Navigation';
import CloseIcon from '@mui/icons-material/Close';
import MenuIcon from '@mui/icons-material/Menu';
import { useNavigate } from 'react-router-dom';
import styles from './Dashboard.module.css';
import authService from '../../api-authorization/AuthorizeService';
import {
  format,
  getCoordinatesFromPolygonText,
  getPolygonCentroid
} from '../../../utils';

const defaultCenter = { lat: 36.55, lng: -119.75 };

function getPolygonCenter(paths) {
  if (!paths || !paths.length) return defaultCenter;
  const lat = paths.reduce((sum, p) => sum + p.lat, 0) / paths.length;
  const lng = paths.reduce((sum, p) => sum + p.lng, 0) / paths.length;
  return { lat, lng };
}

function getSprayerStatusColor(sprayer) {
  const now = new Date();
  const lastSeen = new Date(sprayer.eventDateUtc);
  const msAgo = now.getTime() - lastSeen.getTime();
  const daysAgo = msAgo / (1000 * 60 * 60 * 24);

  if (sprayer.isRunning) {
    if (sprayer.isSpraying) return '#00C853'; // Green
    return '#00B8D4'; // Cyan
  }
  if (daysAgo <= 1) return '#FFD600'; // Yellow
  if (daysAgo <= 7) return '#FF9100'; // Orange
  if (daysAgo <= 31) return '#D50000'; // Red
  return '#8E24AA'; // Purple
}

function getSprayedColor(percent) {
  if (percent < 40) return '#ff5252'; // red
  if (percent < 70) return '#ffeb3b'; // yellow
  if (percent > 105) return '#2196f3'; // blue
  return '#43a047'; // green
}

const iconButtonStyle = { cursor: 'pointer' };

function formatLocalDate(date) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

function Dashboard() {
  const [farms, setFarms] = useState([]);
  const [blocks, setBlocks] = useState([]);
  const [selectedFarm, setSelectedFarm] = useState('');
  const userIdRef = useRef(null);
  const [selectedBlock, setSelectedBlock] = useState('');
  const [sortBy, setSortBy] = useState('lastSeen');
  const [sortDirection, setSortDirection] = useState('desc');
  const [mapCenter, setMapCenter] = useState(defaultCenter);
  const [mapZoom, setMapZoom] = useState(15);
  const mapRef = useRef(null);
  const [activeSprayers, setActiveSprayers] = useState([]);
  const [sprayers] = useState([]);
  const [popup, setPopup] = useState(null);
  const [showBlockNames, setShowBlockNames] = useState(false);
  const isMobile = useMediaQuery('(max-width:600px)');
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [sprayerFilter, setSprayerFilter] = useState('');
  const [isMapOptionsBoxOpen, setIsMapOptionsBoxOpen] = useState(true);
  const [mapTypeId, setMapTypeId] = useState('satellite');
  const [selectedSprayerIds, setSelectedSprayerIds] = useState([]);
  const [rightClickInfo, setRightClickInfo] = useState(null);

  // Date pickers
  const today = new Date();
  const janFirst = new Date(today.getFullYear(), 0, 1);
  const [dateFrom, setDateFrom] = useState(formatLocalDate(janFirst));
  const [dateTo, setDateTo] = useState(formatLocalDate(today));

  // SubRuns state
  const [subRuns, setSubRuns] = useState([]);

  // Grouped subRuns by subLocationId
  const subRunsGrouped = useMemo(() => {
    const grouped = {};
    subRuns.forEach(run => {
      const subLocationId = run.subLocationId;
      if (!grouped[subLocationId]) {
        grouped[subLocationId] = {
          subLocationId,
          totalAreaSprayedAcres: 0,
          runs: []
        };
      }
      grouped[subLocationId].totalAreaSprayedAcres += Number(run.totalAreaSprayedAcres) || 0;
      grouped[subLocationId].runs.push(run);
    });
    return grouped;
  }, [subRuns]);

  const initialFarmSet = useRef(false);
  const lastFarmRef = useRef('');

  // Map sizing (avoid parent-dependent 100%)
  const mapContainerStyle = isMobile
    ? { width: '100%', height: '60vh', minHeight: 350, borderRadius: '12px', overflow: 'hidden' }
    : { width: '100%', height: '900px', borderRadius: '20px', overflow: 'hidden' };

  const navigate = useNavigate();

  // Fetch blocks and derive farms from subLocations response
  useEffect(() => {
    const fetchBlocksAndFarms = async () => {
      try {
        const token = await authService.getAccessToken();
        const headers = {
          headers: !token ? {} : { Authorization: `Bearer ${token}` }
        };

        const blocksRes = await fetch(`${process.env.REACT_APP_BASE_URL}/api/sublocations/all`, headers);
        const blocksData = await blocksRes.json();
        setBlocks(blocksData);

        // Use clientId from the first sublocation to namespace the localStorage key
        const clientId = blocksData.length > 0 ? blocksData[0].clientId : 'default';
        userIdRef.current = clientId;
        const stored = localStorage.getItem(`dashboard:selectedFarm:${clientId}`);
        if (stored) setSelectedFarm(stored);
        // Derive unique farmName values from blocksData and sort by name
        const farmNames = Array.from(
          new Set(
            blocksData
              .map(b => b.farmName)
              .filter(name => !!name && typeof name === 'string')
          )
        );
        // Use farmName as id and name for dropdown, sorted alphabetically
        setFarms(
          farmNames
            .map(name => ({ id: name, name }))
            .sort((a, b) => a.name.localeCompare(b.name))
        );
      } catch (err) {
        setFarms([]);
        setBlocks([]);
      }
    };
    fetchBlocksAndFarms();
  }, []);

  // Fetch active sprayers (no activeSince)
  useEffect(() => {
    const fetchActiveSprayers = async () => {
      try {
        const token = await authService.getAccessToken();
        const headers = {
          headers: !token ? {} : { Authorization: `Bearer ${token}` }
        };
        const url = `${process.env.REACT_APP_BASE_URL}/api/tractors/most-recent-locations`;
        const res = await fetch(url, headers);
        const data = await res.json();
        const sprayers = Array.isArray(data.recentTractorLocations) ? data.recentTractorLocations : [];
        setActiveSprayers(sprayers);

        // Select sprayers seen within the last month by default only if none are selected yet
        setSelectedSprayerIds(prev =>
          prev.length === 0
            ? sprayers
                .filter(s => {
                  const msAgo = Date.now() - new Date(s.eventDateUtc).getTime();
                  return msAgo <= 31 * 24 * 60 * 60 * 1000;
                })
                .map(s => s.displayId)
            : prev
        );
      } catch {
        setActiveSprayers([]);
      }
    };

    fetchActiveSprayers();
    const intervalId = setInterval(fetchActiveSprayers, 10000);

    return () => clearInterval(intervalId);
  }, []);

  // Fetch subRuns by date on load and when dateFrom/dateTo changes
  useEffect(() => {
    const fetchSubRuns = async () => {
      try {
        const token = await authService.getAccessToken();
        const headers = {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {})
        };
        const body = JSON.stringify({
          dateFrom: new Date(dateFrom).toISOString(),
          dateTo: new Date(dateTo).toISOString()
        });
        const res = await fetch(`${process.env.REACT_APP_BASE_URL}/api/subruns/by-date`, {
          method: 'POST',
          headers,
          body
        });
        if (!res.ok) throw new Error('Failed to fetch subRuns');
        const data = await res.json();
        setSubRuns(Array.isArray(data) ? data : []);
      } catch (err) {
        setSubRuns([]);
      }
    };
    fetchSubRuns();
  }, [dateFrom, dateTo]);

  const { isLoaded } = useJsApiLoader({
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_API_KEY || 'YOUR_GOOGLE_MAPS_API_KEY'
  });

  const filteredBlocks = useMemo(() => {
    if (!selectedFarm) return [];
    return blocks.filter(b => b.farmName === selectedFarm);
  }, [blocks, selectedFarm]);

  useEffect(() => {
    if (
      filteredBlocks.length > 0 &&
      selectedFarm &&
      lastFarmRef.current !== selectedFarm
    ) {
      setSelectedBlock(filteredBlocks[0].id);
      lastFarmRef.current = selectedFarm;
    }
    if (!selectedFarm) {
      setSelectedBlock('');
      lastFarmRef.current = '';
    }
  }, [filteredBlocks, selectedFarm]);

  // Persist farm selection to localStorage (keyed by user) whenever it changes
  useEffect(() => {
    if (selectedFarm && userIdRef.current) {
      localStorage.setItem(`dashboard:selectedFarm:${userIdRef.current}`, selectedFarm);
    }
  }, [selectedFarm]);

  useEffect(() => {
    if (
      farms.length > 0 &&
      !initialFarmSet.current
    ) {
      // If stored farm is no longer valid, fall back to first farm
      if (selectedFarm && !farms.some(f => f.id === selectedFarm)) {
        setSelectedFarm(farms[0].id);
      } else if (!selectedFarm) {
        setSelectedFarm(farms[0].id);
      }
      initialFarmSet.current = true;
    }
  }, [farms, selectedFarm]);

  const selectedBlockObj = useMemo(
    () => filteredBlocks.find(b => b.id === selectedBlock) || null,
    [filteredBlocks, selectedBlock]
  );

  const selectedFarmObj = useMemo(
    () => farms.find(f => f.id === selectedFarm) || null,
    [farms, selectedFarm]
  );

  // --- Center map to selected block ---
  useEffect(() => {
    if (selectedBlockObj && selectedBlockObj.boundaries) {
      const polygons = getCoordinatesFromPolygonText(selectedBlockObj.boundaries);
      if (polygons && polygons.length && polygons[0].length) {
        setMapCenter(getPolygonCenter(polygons[0]));
        setMapZoom(18);
        if (mapRef.current && window.google && window.google.maps) {
          const bounds = new window.google.maps.LatLngBounds();
          polygons.forEach(polygon => {
            polygon.forEach(coord => bounds.extend(coord));
          });
          mapRef.current.fitBounds(bounds);
        }
      }
    }
  }, [selectedBlockObj]);

  const sortedActiveSprayers = useMemo(() => {
    const sorted = [...activeSprayers];
    sorted.sort((a, b) => {
      if (sortBy === 'name') {
        const nameA = a.name?.toLowerCase() || '';
        const nameB = b.name?.toLowerCase() || '';
        if (nameA < nameB) return sortDirection === 'asc' ? -1 : 1;
        if (nameA > nameB) return sortDirection === 'asc' ? 1 : -1;
        return 0;
      } else {
        // lastSeen (eventDateUtc)
        const dateA = new Date(a.eventDateUtc);
        const dateB = new Date(b.eventDateUtc);
        return sortDirection === 'asc' ? dateA - dateB : dateB - dateA;
      }
    });
    return sorted;
  }, [activeSprayers, sortBy, sortDirection]);

  const filteredSprayers = useMemo(() =>
    sortedActiveSprayers.filter(s =>
      s.name?.toLowerCase().includes(sprayerFilter.toLowerCase())
    ),
    [sortedActiveSprayers, sprayerFilter]
  );

  const selectedSprayers = useMemo(
    () => filteredSprayers.filter(s => selectedSprayerIds.includes(s.displayId)),
    [filteredSprayers, selectedSprayerIds]
  );

  const prevSprayerIdsRef = useRef([]);
  const sprayerInitialZoomDone = useRef(false);

  useEffect(() => {
    if (!isLoaded || !mapRef.current || selectedSprayerIds.length === 0) return;
    if (!window.google || !window.google.maps) return;

    // Only fit bounds if the selection of sprayer IDs has changed
    const prevIds = prevSprayerIdsRef.current;
    const idsChanged =
      prevIds.length !== selectedSprayerIds.length ||
      prevIds.some((id, i) => id !== selectedSprayerIds[i]);

    if (idsChanged) {
      // Skip the initial sprayer zoom if a farm is already selected — the block
      // zoom will handle positioning. Only zoom to sprayers on subsequent manual
      // selection changes after the initial load.
      const isInitialLoad = prevIds.length === 0;
      if (!isInitialLoad || !selectedFarm) {
        const bounds = new window.google.maps.LatLngBounds();
        let hasValid = false;
        selectedSprayers.forEach(sprayer => {
          if (sprayer.lat && sprayer.long) {
            bounds.extend({ lat: Number(sprayer.lat), lng: Number(sprayer.long) });
            hasValid = true;
          }
        });
        if (hasValid) {
          mapRef.current.fitBounds(bounds);
        }
      }
      prevSprayerIdsRef.current = [...selectedSprayerIds];
    }
  }, [selectedSprayerIds, isLoaded, selectedSprayers, selectedFarm]);

  const handleMapLoad = (map) => {
    mapRef.current = map;
  };

  const handleMapRightClick = (e) => {
    setRightClickInfo({ lat: e.latLng.lat(), lng: e.latLng.lng() });
  };

  const handleCopyCoords = () => {
    if (rightClickInfo) {
      navigator.clipboard.writeText(`${rightClickInfo.lat}, ${rightClickInfo.lng}`);
      setRightClickInfo(null);
    }
  };

  const handleFindOnMap = (lat, lng) => {
    const latNum = Number(lat);
    const lngNum = Number(lng);
    setMapCenter({ lat: latNum, lng: lngNum });
    setMapZoom(20);
    if (mapRef.current && window.google && window.google.maps) {
      mapRef.current.panTo({ lat: latNum, lng: lngNum });
      mapRef.current.setZoom(20);
    }
  };

  const handleGoToRun = (sprayer) => {
    if (!sprayer || !sprayer.name || !sprayer.tractorId || !sprayer.runId) return;
    const url = `/precision-spray/${sprayer.name.replace(/ /g, "-")}/${sprayer.tractorId}/run-details/${sprayer.runId}/${sprayer.displayId}`;
    window.open(url, "_blank");
  };

  const handlePolygonClick = (event, block) => {
    if (event.latLng) setRightClickInfo({ lat: event.latLng.lat(), lng: event.latLng.lng() });
    navigate(
      `/dashboard/block-details/${block.name.toLowerCase().replaceAll(" ", "-")}/${block.id}`,
      { state: { dateFrom, dateTo } }
    );
  };

  const handlePopupClose = () => setPopup(null);

  const handleSprayerCheckbox = (sprayerId) => (e) => {
    if (e.target.checked) {
      setSelectedSprayerIds(prev => [...prev, sprayerId]);
    } else {
      setSelectedSprayerIds(prev => prev.filter(id => id !== sprayerId));
    }
  };

  const handleSort = (column) => {
    if (sortBy === column) {
      setSortDirection(prev => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortBy(column);
      setSortDirection('asc');
    }
  };

  // Helper to get all visible sprayer IDs (filteredSprayers)
  const allVisibleSprayerIds = useMemo(
    () => filteredSprayers.map(s => s.displayId),
    [filteredSprayers]
  );

  // Determine if all are selected or some are selected
  const allSelected = allVisibleSprayerIds.length > 0 && allVisibleSprayerIds.every(id => selectedSprayerIds.includes(id));
  const someSelected = allVisibleSprayerIds.some(id => selectedSprayerIds.includes(id)) && !allSelected;

  // Handler for Select All checkbox
  const handleSelectAllSprayers = (e) => {
    if (e.target.checked) {
      setSelectedSprayerIds(allVisibleSprayerIds);
    } else {
      setSelectedSprayerIds([]);
    }
  };

  const controls = (
    <Box
      sx={{
        minWidth: isMapOptionsBoxOpen ? 180 : 160,
        width: isMapOptionsBoxOpen ? 275 : 160,
        bgcolor: '#fff',
        borderRadius: 2,
        boxShadow: '0 2px 8px rgba(0,0,0,0.10)',
        zIndex: 10,
        overflow: 'hidden',
        transition: 'width 0.2s, min-width 0.2s',
      }}
    >
      <Box
        onClick={() => setIsMapOptionsBoxOpen(open => !open)}
        sx={{
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          p: '6px 12px',
          bgcolor: '#f5f5f5',
          fontWeight: 400,
          fontSize: 16,
          borderBottom: isMapOptionsBoxOpen ? '1px solid #eee' : 'none',
          minHeight: 40,
          height: 40,
          transition: 'all 0.2s',
        }}
      >
        <Typography
          variant="subtitle1"
          component="span"
          sx={{
            fontWeight: 500,
            fontSize: '1rem',
            color: 'text.primary',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            flex: 1,
            transition: 'font-size 0.2s',
          }}
        >
          Map Options
        </Typography>
        {isMapOptionsBoxOpen ? <ChevronLeft /> : <ChevronRight />}
      </Box>
      {isMapOptionsBoxOpen && (
        <Box sx={{ p: 2.5 }}>
          <FormControlLabel
            control={
              <Checkbox
                checked={showBlockNames}
                onChange={e => setShowBlockNames(e.target.checked)}
                color="primary"
              />
            }
            label={
              <Typography variant="body1" sx={{ fontWeight: 500, fontSize: '1rem' }}>
                Show Block Names
              </Typography>
            }
          />
          <Button
            variant="contained"
            color="primary"
            sx={{ width: '100%', mt: 2 }}
            onClick={() => {
              if (!mapRef.current || !window.google || !window.google.maps) return;
              const bounds = new window.google.maps.LatLngBounds();
              let hasValid = false;
              selectedSprayers.forEach(sprayer => {
                if (sprayer.lat && sprayer.long) {
                  bounds.extend({ lat: Number(sprayer.lat), lng: Number(sprayer.long) });
                  hasValid = true;
                }
              });
              if (hasValid) {
                mapRef.current.fitBounds(bounds);
              }
            }}
          >
            See All Sprayers
          </Button>
        </Box>
      )}
    </Box>
  );

  return (
    <div className={styles.container} style={{ alignItems: 'stretch', background: 'white' }}>
      {/* Top Controls */}
      <div
        style={{
          display: 'flex',
          flexDirection: isMobile ? 'column' : 'row',
          alignItems: isMobile ? 'stretch' : 'flex-end',
          marginBottom: 24,
          gap: isMobile ? 12 : 32
        }}
      >
        {/* Select Farm */}
        <div style={{ display: 'flex', flexDirection: 'column', minWidth: 220, width: isMobile ? '100%' : 220 }}>
          <Typography variant="h6" sx={{ marginBottom: 0.5 }}>
            <strong>Farm</strong>
          </Typography>
          <Autocomplete
            size="small"
            fullWidth
            options={farms}
            getOptionLabel={option => option?.name || ''}
            value={selectedFarmObj}
            onChange={(_, newValue) => {
              const newFarmId = newValue ? newValue.id : '';
              setSelectedFarm(newFarmId);

              if (newFarmId) {
                const firstBlock = blocks.find(b => b.farmName === newFarmId);
                setSelectedBlock(firstBlock ? firstBlock.id : '');
              } else {
                setSelectedBlock('');
              }
            }}
            renderInput={params => (
              <FormControl size="small" fullWidth>
                <InputLabel htmlFor="farm-autocomplete" shrink />
                <div ref={params.InputProps.ref}>
                  <input
                    {...params.inputProps}
                    id="farm-autocomplete"
                    style={{
                      width: '100%',
                      padding: '8.5px 14px',
                      fontSize: '1rem',
                      border: '1px solid #c4c4c4',
                      borderRadius: 4
                    }}
                    placeholder="Type to search farms..."
                  />
                </div>
              </FormControl>
            )}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            disabled={farms.length === 0}
            clearOnEscape
            clearOnBlur
          />
        </div>

        {/* Block / From / To (each on its own line on mobile) */}
        <div style={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', alignItems: isMobile ? 'stretch' : 'flex-end', gap: 16, width: isMobile ? '100%' : 'auto' }}>
          {/* Block Dropdown */}
          <div style={{ width: isMobile ? '100%' : 'auto' }}>
            <Typography variant="h6" sx={{ marginBottom: 0.5 }}>
              <strong>Block</strong>
            </Typography>
            <Autocomplete
              size="small"
              fullWidth
              options={filteredBlocks}
              getOptionLabel={option => option?.name || ''}
              value={selectedBlockObj}
              onChange={(_, newValue) => setSelectedBlock(newValue ? newValue.id : '')}
              renderInput={params => (
                <FormControl size="small" fullWidth>
                  <InputLabel htmlFor="block-autocomplete" shrink />
                  <div ref={params.InputProps.ref}>
                    <input
                      {...params.inputProps}
                      id="block-autocomplete"
                      style={{
                        width: '100%',
                        padding: '8.5px 14px',
                        fontSize: '1rem',
                        border: '1px solid #c4c4c4',
                        borderRadius: 4
                      }}
                      placeholder="Type to search blocks..."
                    />
                  </div>
                </FormControl>
              )}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              disabled={!selectedFarm}
              clearOnEscape
              clearOnBlur
            />
          </div>

          {/* From Date Picker */}
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', width: isMobile ? '100%' : 'auto' }}>
            <span
              style={{
                fontWeight: 600,
                fontSize: '1.25rem',
                marginBottom: 2,
                display: 'flex',
                alignItems: 'center'
              }}
            >
              From
            </span>
            <TextField
              type="date"
              size="small"
              value={dateFrom}
              onChange={e => setDateFrom(e.target.value)}
              inputProps={{ max: dateTo }}
              fullWidth
            />
          </div>

          {/* To Date Picker */}
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', width: isMobile ? '100%' : 'auto' }}>
            <span
              style={{
                fontWeight: 600,
                fontSize: '1.25rem',
                marginBottom: 2,
                display: 'flex',
                alignItems: 'center'
              }}
            >
              To
            </span>
            <TextField
              type="date"
              size="small"
              value={dateTo}
              onChange={e => setDateTo(e.target.value)}
              inputProps={{ min: dateFrom, max: formatLocalDate(today) }}
              fullWidth
            />
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: isMobile ? '1fr' : '1fr 460px', // CHANGED: removed sidebarCollapsed condition
          gap: isMobile ? 12 : 24,
          width: '100%',
          minHeight: 0,
          overflow: 'hidden'
        }}
      >
        {/* Map Area */}
        <div
          className={styles.mapContainer}
          style={{
            width: '100%',
            height: isMobile ? '60vh' : '900px',
            minHeight: isMobile ? 350 : undefined,
            position: 'relative',
            borderRadius: isMobile ? 12 : 20,
            margin: 0,
            padding: 0,
              boxSizing: 'border-box',
                overflow: 'hidden'
              }}
            >
          {/* Block Names Toggle Drawer/Button */}
          {isMobile ? (
            <>
              <IconButton
                style={{
                  position: 'absolute',
                  top: 10,
                  right: 10,
                  zIndex: 10001,
                  background: '#fff',
                  boxShadow: 1
                }}
                onClick={() => setDrawerOpen(true)}
                size="large"
              >
                <MenuIcon />
              </IconButton>
              <Drawer
                anchor="right"
                open={drawerOpen}
                onClose={() => setDrawerOpen(false)}
              >
                <Box sx={{ p: 2 }}>
                  {controls}
                </Box>
              </Drawer>
            </>
          ) : (
            <div
              style={{
                position: 'absolute',
                top: 10,
                right: 10,
                zIndex: 10000
              }}
            >
              {controls}
            </div>
          )}
          {isLoaded && (
            <GoogleMap
              mapContainerStyle={mapContainerStyle}
              center={mapCenter}
              zoom={mapZoom}
              onLoad={handleMapLoad}
              onRightClick={handleMapRightClick}
              onClick={(e) => { if (e.latLng) setRightClickInfo({ lat: e.latLng.lat(), lng: e.latLng.lng() }); }}
              options={{
                mapTypeId: mapTypeId,
                streetViewControl: false,
                fullscreenControl: false,
                mapTypeControl: true,
                mapTypeControlOptions: {
                  style: window.google && window.google.maps
                    ? window.google.maps.MapTypeControlStyle.DROPDOWN_MENU
                    : undefined,
                  position: window.google && window.google.maps
                    ? window.google.maps.ControlPosition.TOP_LEFT
                    : undefined
                }
              }}
              onMapTypeIdChanged={() => {
                if (mapRef.current) {
                  const newType = mapRef.current.getMapTypeId();
                  setMapTypeId(newType);
                }
              }}
            >
              {/* --- Render polygons and centroids for all polygons --- */}
              {filteredBlocks.map(block => {
                const group = subRunsGrouped[block.id];
                const area = group ? group.totalAreaSprayedAcres : 0;
                const blockArea = block.areaAcres || 1;
                const percent = Math.round((area / blockArea) * 100);
                const fillColor = getSprayedColor(percent);

                return block.boundaries ? (
                  <React.Fragment key={block.id}>
                    {getCoordinatesFromPolygonText(block.boundaries).map((polygon, polyIdx) => (
                      <Polygon
                        key={`${block.id}-${polyIdx}`}
                        paths={polygon}
                        options={{
                          fillColor,
                          fillOpacity: 0.5,
                          strokeColor: '#333',
                          strokeOpacity: 0.95,
                          strokeWeight: 3,
                          clickable: true,
                          zIndex: 1
                        }}
                        onClick={event => handlePolygonClick(event, block)}
                      />
                    ))}
                    {showBlockNames && getCoordinatesFromPolygonText(block.boundaries).map((polygon, polyIdx) => {
                      const centroid = getPolygonCentroid(polygon);
                      return (
                        <OverlayViewF
                          key={`label-${block.id}-${polyIdx}`}
                          position={centroid}
                          mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
                        >
                          <div style={{
                            transform: 'translate(-50%, -50%)',
                            textAlign: 'center',
                            fontWeight: 'bold',
                            fontSize: '16px',
                            color: '#222',
                            whiteSpace: 'nowrap',
                            pointerEvents: 'none',
                            textShadow: '0 0 4px #fff, 0 0 4px #fff'
                          }}>
                            <div>{block.name}</div>
                            <div>{percent}% sprayed</div>
                          </div>
                        </OverlayViewF>
                      );
                    })}
                  </React.Fragment>
                ) : null;
              })}
              {/* Popup for block details */}
              {popup && (
                <InfoWindow
                  position={popup.position}
                  onCloseClick={handlePopupClose}
                >
                  <div>
                    Block Details For {popup.name}
                  </div>
                </InfoWindow>
              )}
              {/* Render markers for active sprayers */}
              {filteredSprayers
                .filter(sprayer => selectedSprayerIds.includes(sprayer.displayId))
                .map(sprayer => (
                  <Marker
                    key={sprayer.displayId || sprayer.runId || sprayer.name}
                    position={{ lat: Number(sprayer.lat), lng: Number(sprayer.long) }}
                    label={sprayer.name}
                    onClick={() =>
                      window.open(
                        `/precision-spray/${sprayer.name.replace(/ /g, "-")}/${sprayer.tractorId}`,
                        "_blank"
                      )
                    }
                    icon={{
                      path: window.google && window.google.maps
                        ? window.google.maps.SymbolPath.FORWARD_CLOSED_ARROW
                        : undefined,
                      scale: 6,
                      fillColor: getSprayerStatusColor(sprayer),
                      fillOpacity: 1,
                      strokeColor: '#333',
                      strokeWeight: 2,
                      anchor: new window.google.maps.Point(0, 2)
                    }}
                  />
                ))}
              {/* Render static sprayers if needed */}
              {sprayers.map(sprayer => (
                <Marker
                  key={sprayer.id}
                  position={sprayer.position}
                  label={sprayer.name}
                  onClick={() => alert(`Go to Sprayer Details for ${sprayer.name}`)}
                  icon={{
                    path: window.google && window.google.maps
                      ? window.google.maps.SymbolPath.FORWARD_CLOSED_ARROW
                      : undefined,
                    scale: 6,
                    fillColor: '#ff9800',
                    fillOpacity: 1,
                    strokeColor: '#333',
                    strokeWeight: 2,
                    anchor: new window.google.maps.Point(0, 2)
                  }}
                />
              ))}
              {rightClickInfo && (
                <InfoWindow position={rightClickInfo} onCloseClick={() => setRightClickInfo(null)}>
                  <Box sx={{ position: 'relative', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5, pt: 2, pb: 1, px: 1, minWidth: 120 }}>
                    <IconButton onClick={() => setRightClickInfo(null)} aria-label="Close" sx={{ position: 'absolute', top: 0, right: 0, p: 0.5 }}>
                      <CloseIcon sx={{ fontSize: 20 }} />
                    </IconButton>
                    <Typography sx={{ fontSize: 14, fontWeight: 600 }}>Location</Typography>
                    <Typography sx={{ fontSize: 13 }}>Lat: {rightClickInfo.lat.toFixed(6)}</Typography>
                    <Typography sx={{ fontSize: 13 }}>Long: {rightClickInfo.lng.toFixed(6)}</Typography>
                    <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                      <IconButton onClick={handleCopyCoords} aria-label="Copy coordinates" title="Copy coordinates">
                        <ContentCopyIcon />
                      </IconButton>
                      {isMobile && (
                        <IconButton
                          component="a"
                          href={`https://maps.google.com/maps?daddr=${rightClickInfo.lat},${rightClickInfo.lng}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          aria-label="Navigate to coordinates"
                          title="Navigate"
                        >
                          <NavigationIcon />
                        </IconButton>
                      )}
                    </Box>
                  </Box>
                </InfoWindow>
              )}
            </GoogleMap>
          )}
        </div>

        {/* Sidebar */}
        <div
          style={{
            background: 'white',
            border: '2px solid #222',
            borderRadius: 8,
            padding: isMobile ? 12 : 24,
            height: isMobile ? 600 : 900,
            display: 'flex',
            flexDirection: 'column',
            position: 'relative',
            marginTop: isMobile ? 16 : 0,
            overflow: 'hidden'
          }}
        >
          {/* Content (no longer conditionally rendered) */}
          <div style={{ flex: 1, border: 'none', display: 'flex', flexDirection: 'column', minHeight: 0 }}>
            {/* Heading */}
            <div
              style={{
                marginBottom: 8,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 12
              }}
            >
              <span style={{ fontWeight: 700, fontSize: 26, color: '#222', display: 'block', whiteSpace: 'nowrap' }}>
                Sprayers
              </span>
            </div>
            {/* Status Legend */}
            <div
              style={{
                border: '1.5px solid #ddd',
                borderRadius: 8,
                padding: '10px 16px',
                margin: '8px 0 4px 0',
                background: '#fafbfc',
                display: 'grid',
                gridTemplateColumns: '1fr 1fr',
                rowGap: 8,
                columnGap: 24,
                alignItems: 'center',
                justifyItems: 'start'
              }}
            >
              <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                <span style={{
                  display: 'inline-block',
                  width: 14,
                  height: 14,
                  borderRadius: '50%',
                  background: '#00C853',
                  border: '1.5px solid #eee'
                }} />
                <span style={{ fontSize: 13 }}>Running &amp; Spraying</span>
              </span>
              <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                <span style={{
                  display: 'inline-block',
                  width: 14,
                  height: 14,
                  borderRadius: '50%',
                  background: '#00B8D4',
                  border: '1.5px solid #eee'
                }} />
                <span style={{ fontSize: 13 }}>Running, Not Spraying</span>
              </span>
              <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                <span style={{
                  display: 'inline-block',
                  width: 14,
                  height: 14,
                  borderRadius: '50%',
                  background: '#FFD600',
                  border: '1.5px solid #eee'
                }} />
                <span style={{ fontSize: 13 }}>Not Running, &lt;1 day</span>
              </span>
              <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                <span style={{
                  display: 'inline-block',
                  width: 14,
                  height: 14,
                  borderRadius: '50%',
                  background: '#FF9100',
                  border: '1.5px solid #eee'
                }} />
                <span style={{ fontSize: 13 }}>Not Running, &lt;1 week</span>
              </span>
              <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                <span style={{
                  display: 'inline-block',
                  width: 14,
                  height: 14,
                  borderRadius: '50%',
                  background: '#D50000',
                  border: '1.5px solid #eee'
                }} />
                <span style={{ fontSize: 13 }}>Not Running, &lt;1 month</span>
              </span>
              <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                <span style={{
                  display: 'inline-block',
                  width: 14,
                  height: 14,
                  borderRadius: '50%',
                  background: '#8E24AA',
                  border: '1.5px solid #eee'
                }} />
                <span style={{ fontSize: 13 }}>Not Running, &gt;1 month</span>
              </span>
            </div>
            <Divider style={{ margin: '8px 0 0 0' }} />
            <div style={{ margin: '12px 0' }}>
              <input
                type="text"
                value={sprayerFilter}
                onChange={e => setSprayerFilter(e.target.value)}
                placeholder="Filter sprayers by name..."
                style={{
                  width: '100%',
                  padding: '6px 10px',
                  borderRadius: 4,
                  border: '1px solid #ccc',
                  fontSize: 14
                }}
              />
            </div>
            <FormControlLabel
              control={
                <Checkbox
                  checked={allSelected}
                  indeterminate={someSelected}
                  onChange={handleSelectAllSprayers}
                  color="primary"
                  disabled={filteredSprayers.length === 0}
                />
              }
              label="Select All"
            />
            {/* Table header */}
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: '0.5fr 1.2fr 2.5fr 3.8fr 0.5fr',
                gap: 8,
                fontWeight: 600,
                marginBottom: 8,
                marginTop: 0
              }}
            >
              <div style={{ textAlign: 'center', justifySelf: 'center' }}></div>
              <div style={{ textAlign: 'center', justifySelf: 'center' }}>Status</div>
              <div
                style={{
                  cursor: 'pointer',
                  userSelect: 'none',
                  textAlign: 'center',
                  justifySelf: 'center'
                }}
                onClick={() => handleSort('name')}
                title="Sort by Name"
              >
                Name {sortBy === 'name' ? (sortDirection === 'asc' ? '▲' : '▼') : ''}
              </div>
              <div
                style={{
                  cursor: 'pointer',
                  userSelect: 'none',
                  textAlign: 'center',
                  justifySelf: 'center'
                }}
                onClick={() => handleSort('lastSeen')}
                title="Sort by Last Seen"
              >
                Last Seen {sortBy === 'lastSeen' ? (sortDirection === 'asc' ? '▲' : '▼') : ''}
              </div>
              <div style={{ textAlign: 'center', justifySelf: 'center' }}>Details</div>
            </div>
            {/* Table rows (scrollable list) */}
            <div
              style={{
                flex: 1,
                minHeight: 0,
                overflowY: 'auto'
              }}
            >
              {filteredSprayers.length === 0 ? (
                <div style={{ color: '#888', fontStyle: 'italic', marginTop: 16 }}>
                  No active sprayers found for the selected period.
                </div>
              ) : (
                filteredSprayers.map(sprayer => (
                  <div
                    key={sprayer.displayId}
                    style={{
                      display: 'grid',
                      gridTemplateColumns: '0.5fr 1.2fr 2.5fr 3.8fr 0.5fr',
                      gap: 8,
                      alignItems: 'center',
                      marginBottom: 4
                    }}
                  >
                    <Checkbox
                      checked={selectedSprayerIds.includes(sprayer.displayId)}
                      onChange={handleSprayerCheckbox(sprayer.displayId)}
                      color="primary"
                    />
                    <span
                      style={{
                        display: 'inline-block',
                        width: 18,
                        height: 18,
                        borderRadius: '50%',
                        background: getSprayerStatusColor(sprayer),
                        border: '2px solid #eee',
                        margin: '0 auto'
                      }}
                      className={sprayer.isRunning && sprayer.isSpraying ? styles.radiantBlink : ''}
                      title="Status"
                    />
                    <button
                      type="button"
                      onClick={() => handleFindOnMap(sprayer.lat, sprayer.long)}
                      style={{
                        background: 'none',
                        border: 'none',
                        padding: 0,
                        margin: 0,
                        textAlign: 'left',
                        color: '#1976d2',
                        textDecoration: 'underline',
                        cursor: 'pointer',
                        font: 'inherit'
                      }}
                      title="Show on map"
                    >
                      {sprayer.name}
                    </button>
                    <div>
                      {format(sprayer.eventDateUtc, "Pp")}
                    </div>
                    <IconButton
                      color="secondary"
                      aria-label="Go to run"
                      onClick={() => handleGoToRun(sprayer)}
                      disabled={!sprayer.name || !sprayer.tractorId || !sprayer.runId}
                      style={iconButtonStyle}
                    >
                      <OpenInNewIcon />
                    </IconButton>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      </div>
          <style>{`.gm-ui-hover-effect { display: none !important; }`}</style>
        </div>
      );
    }

    export default Dashboard;